import { Form } from "antd";
import _ from "lodash";
import React, {
  forwardRef,
  useCallback,
  useMemo,
  useRef,
  useState,
} from "react";
import ToolsIntegrationsAPI from "restAPI/ToolsIntegrationAPI";
import { FieldTypes } from "utils/formUtils";

import GenericMappingForm from "../GenericMappingForm";

const ApiSecMappingForm = forwardRef(
  function ApiSecMappingForm({ disableProjectFields = false, ...props }, ref) {
    const IntRef = useRef({});
    const [form] = Form.useForm();
    
    const [applications, setApplications] = useState(() => {
      if (props.record) {
        const { applicationId, applicationName } = props.record;
        return applicationId
          ? [
              {
                id: applicationId,
                name: applicationName,
              },
            ]
          : [];
      }
      return [];
    });

    const [instances, setInstances] = useState(() => {
        if (props.record) {
            const { instanceId, instanceName } = props.record;

            const instanceIds = _.isEmpty(instanceId) ? [] : instanceId.split(",");
            const instanceNames = _.isEmpty(instanceName) ? [] : instanceName.split(",");

            return instanceIds.map((id, idx) => ({
            id,
            name: instanceNames[idx] || id,
            }));
        }
        return [];
    });


    const setOptionsFromResponse = (option, fetchKey) => {
      if (fetchKey === "instance") {
        setInstances(prev =>
            _.uniqBy([...option, ...prev], "id")
        );
      } else {
        setApplications(option);
      }
    };

    const fetchProjects = useCallback(
      (key, params) => {
        const { promise, cancel } =
          ToolsIntegrationsAPI.getGenericProjectByToolName(props.toolName, {
            login_id: props.loginConfigId,
            ...params,
          });
        const newPromise = promise.then((response) => {
          setOptionsFromResponse(response.projects, key);
          return response.projects;
        });

        return { promise: newPromise, cancel };
      },
      [props.loginConfigId, props.toolName],
    );

    const fetchApplication = useCallback(
      (params) => {
        return fetchProjects("application", params);
      },
      [fetchProjects],
    );

    const fetchInstance = useCallback(
      (params) => {
        return fetchProjects("instance", params);
      },
      [fetchProjects],
    );

    const getExtraFieldsData = useCallback(
      (config) => {
        const { instanceId, applicationId } = config;

        const application = applications.find((p) => p.id === applicationId);
        const applicationName = application?.name;

        const data = instances.reduce(
          (acc, cv) => {
            const { id, name } = cv;
            if (instanceId?.includes(id)) {
              acc.id.push(id);
              acc.name.push(name);
            }
            return acc;
          },
          { id: [], name: [] },
        );

        return {
          applicationId,
          applicationName,
          instanceId: data.id.join(","),
          instanceName: data.name.join(","),
          ...(config.discovery_config_id && {
            discovery_config_id: config.discovery_config_id,
          }),
        };
      },
      [applications, instances],
    );

    const parseExtraFieldsDataFromResponse = useCallback((config) => {
      const { instances, applicationName, applicationId } = config;

      const instancesArray = _.isEmpty(instances) ? [] : instances.split(",");

      return {
        applicationName,
        versionId: instancesArray,
        applicationId,
        ...(config.discovery_config_id && {
          discovery_config_id: config.discovery_config_id,
        }),
      };
    }, []);

    const onApplicationIdChange = useCallback(
      (value, field) => {
        const { name } = field || {};
        const configurations = form.getFieldValue("configurations");
        const updatedConfiguration = [...configurations];
        updatedConfiguration[name] = {
          ...updatedConfiguration[name],
          instanceId: undefined,
        };
        form.setFieldsValue({ configurations: updatedConfiguration });
      },
      [form],
    );

    const extraFields = useMemo(
      () => [
        {
          name: "applicationId",
          colSpan: "12",
          type: FieldTypes.SELECT,
          label: "APISec Application",
          disabled: disableProjectFields,
          required: true,
          props: {
            api: fetchApplication,
            allowClear: true,
            onChange: onApplicationIdChange,
            autoFocus: !disableProjectFields,
            placeholder: "Select Application...",
          },
        },
        {
          name: "instanceId",
          colSpan: 12,
          type: FieldTypes.MULTI_SELECT,
          label: "Instance",
          disabled: disableProjectFields,
          required: true,
          props: {
            setExtraProps: (fieldProps, currentField) => {
              const configurations = form.getFieldValue("configurations");
              const currentConfiguration =
                configurations[currentField.name] || {};
              const applicationId = currentConfiguration?.applicationId;

              if (applicationId) {
                const application = applications.find((p) => p.id === applicationId);
                if (application && !IntRef.current[applicationId]) {
                  IntRef.current[applicationId] = {
                    project: applicationId,
                  };
                }
              }

              return {
                api: applicationId && fetchInstance,
                allowClear: true,
                apiParams: IntRef.current[applicationId],
                resetOptionsProp: IntRef.current[applicationId],
              };
            },
            placeholder: "Select Instances...",
          },
        },
      ],
      [disableProjectFields, fetchApplication, onApplicationIdChange, form, fetchInstance, applications],
    );

    return (
      <GenericMappingForm
        ref={ref}
        form={form}
        getExtraFieldsData={getExtraFieldsData}
        parseExtraFieldsDataFromResponse={parseExtraFieldsDataFromResponse}
        extraFields={extraFields}
        {...props}
      />
    );
  },
);

export default ApiSecMappingForm;